import { Avatar, Badge, Box, Card, Stack, Typography } from '@mui/material';

import { radius } from '@karehero/llama/theme/radius';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { Logo } from '@karehero/llama/components/atoms/Logo/Logo';

import { SectionTitle } from '@/designs-dev/handover/Panel';
import PhoneFrame from '@/designs-dev/device/PhoneFrame';
import StatusBar from '@/designs-dev/device/StatusBar';
import ChatActionRow from '@/components/wrapped/ChatActionRow';
import ChatTyping from '@/components/wrapped/ChatTyping';
import HomeScreen from '@/karehero-app/home/HomeScreen';
import ChatBubble from '@/karehero-app/tell-us-once/intake/ChatBubble';
import QuickReplies from '@/karehero-app/tell-us-once/intake/QuickReplies';
import {
  IntakeStyleContext,
  ONBOARDING_2_0_STYLE,
} from '@/karehero-app/tell-us-once/intake/IntakeStyle';
import { Frame } from '@/karehero-app/site-mockups/SiteMockups';
import {
  BANK_PULLOUTS,
  BANNERS,
  EMPLOYER_PULLOUTS,
  EXPERT,
  GENERAL_PULLOUTS,
  HOME_SERVICES,
  INCOMING,
  SECTIONS,
  SERVICE_CLUSTERS,
  type BannerLayout,
  type ChatLine,
  type Piece,
  type Picture,
} from '@/karehero-app/site-headers/SiteHeaders.content';

// Website imagery, from Chloe's Miro brief: four sets of pictures for the site
// refresh, every one on a clear ground so Zippy can lay it over stock imagery
// and the new site aesthetic.
//
// Feature pulls, not screenshots. Each picture is a few fragments of the kind
// of thing a member sees (a message from their care expert, a push, a calendar
// pop up, a face on a call) and none of them walks through a flow. The one
// whole screen is the app behind the main banners, and that is blurred to a
// shape on purpose.
//
// The chat is the app's chat: ChatBubble, QuickReplies, ChatActionRow and
// ChatTyping, with 2.0's gardenia replies. Only the frames around it are drawn
// here.

// One card padding for every surface here.
const PAD = 4; // 20px

const ExpertAvatar = ({ size = 40 }: { size?: number }) => (
  <Avatar
    alt={EXPERT.name}
    sx={{
      width: size,
      height: size,
      bgcolor: 'custom.limeGreen.500',
      color: 'custom.marineGreen.700',
    }}
  >
    {EXPERT.name[0]}
  </Avatar>
);

/** Title, then one supporting line: the pair every card here carries. */
const TitleBody = ({ title, body }: { title: string; body: string }) => (
  <Stack spacing={1}>
    <Typography variant='h5'>{title}</Typography>
    <Typography sx={{ color: 'text.secondary' }} variant='body2'>
      {body}
    </Typography>
  </Stack>
);

/** A push notification, as it lands on a lock screen. */
const Push = ({
  title,
  body,
  when,
}: {
  title: string;
  body: string;
  when: string;
}) => (
  <Card sx={{ p: PAD }}>
    <Stack spacing={2}>
      <Stack
        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        direction='row'
      >
        <Logo variant='full' size='sm' />
        <Typography variant='caption'>{when}</Typography>
      </Stack>
      <TitleBody title={title} body={body} />
    </Stack>
  </Card>
);

/** The messages, in the app's bubbles with 2.0's replies. */
const Lines = ({ lines }: { lines: ChatLine[] }) => (
  <IntakeStyleContext.Provider value={ONBOARDING_2_0_STYLE}>
    <Stack spacing={2}>
      {lines.map((line) => (
        <ChatBubble key={line.text} from={line.from} text={line.text} />
      ))}
    </Stack>
  </IntakeStyleContext.Provider>
);

const Replies = ({ replies }: { replies: string[] }) => (
  <QuickReplies
    replies={replies.map((label, i) => ({ label, secondary: i > 0 }))}
    onPick={() => {}}
  />
);

/**
 * A thread with the care expert, on the gardenia the chat screen sits on. A
 * Floral White card would put a bordered bubble inside a bordered surface of
 * the same colour; on gardenia the bubbles read the way they do in the app.
 */
const Chat = ({
  lines,
  replies,
  offer,
  typing,
}: Extract<Piece, { kind: 'chat' }>) => (
  <Stack
    sx={{
      p: PAD,
      bgcolor: 'custom.gardenia',
      border: 1,
      borderColor: 'custom.neutral.200',
      borderRadius: `${radius.lg}px`,
    }}
    spacing={4}
  >
    <Stack sx={{ alignItems: 'center' }} direction='row' spacing={2}>
      <ExpertAvatar />
      <Stack>
        <Typography variant='h5'>{EXPERT.name}</Typography>
        <Typography variant='caption'>{EXPERT.role}</Typography>
      </Stack>
    </Stack>
    <Stack spacing={2}>
      <Lines lines={lines} />
      {typing && <ChatTyping />}
      {offer && <ChatActionRow label={offer} icon='phone' />}
    </Stack>
    {replies && <Replies replies={replies} />}
  </Stack>
);

/**
 * Bubbles with no panel round them, lifted straight out of the thread. Each of
 * the expert's messages carries the avatar beside it, where the app puts the
 * speaker; the member's sit on the right with none.
 */
const Bubbles = ({ lines, replies }: Extract<Piece, { kind: 'bubbles' }>) => (
  <IntakeStyleContext.Provider value={ONBOARDING_2_0_STYLE}>
    <Stack spacing={2}>
      {lines.map((line) =>
        line.from === 'keni' ? (
          <Stack
            key={line.text}
            sx={{ alignItems: 'flex-end' }}
            direction='row'
            spacing={2}
          >
            <ExpertAvatar size={32} />
            <ChatBubble from='keni' text={line.text} />
          </Stack>
        ) : (
          <ChatBubble key={line.text} from='user' text={line.text} />
        ),
      )}
      {replies && (
        <Box sx={{ pl: 7 }}>
          <Replies replies={replies} />
        </Box>
      )}
    </Stack>
  </IntakeStyleContext.Provider>
);

/** A calendar pop up: the date as a tile, then what and when. */
const Reminder = ({
  day,
  month,
  title,
  time,
}: Extract<Piece, { kind: 'reminder' }>) => (
  <Card sx={{ p: PAD }}>
    <Stack sx={{ alignItems: 'center' }} direction='row' spacing={3}>
      <Stack
        sx={{
          width: 52,
          py: 1,
          flexShrink: 0,
          alignItems: 'center',
          bgcolor: 'custom.sunflower.500',
          borderRadius: `${radius.md}px`,
        }}
      >
        <Typography sx={{ color: 'text.primary' }} variant='caption'>
          {month}
        </Typography>
        <Typography variant='h4'>{day}</Typography>
      </Stack>
      <Box sx={{ minWidth: 0 }}>
        <TitleBody title={title} body={time} />
      </Box>
    </Stack>
  </Card>
);

/**
 * The care expert's face, on a call. The face is a placeholder: Zippy drops a
 * stock portrait into the circle, and the frame around it says it is a call.
 */
const CallTile = () => (
  <Stack
    sx={{
      aspectRatio: '3 / 4',
      p: PAD,
      bgcolor: 'custom.marineGreen.700',
      borderRadius: `${radius.lg}px`,
    }}
  >
    <Stack sx={{ alignItems: 'center' }} direction='row' spacing={2}>
      <Box
        sx={{
          width: 8,
          height: 8,
          bgcolor: 'custom.coral.500',
          borderRadius: '50%',
        }}
      />
      <Typography sx={{ color: 'custom.neutral.50' }} variant='body2'>
        04:12
      </Typography>
    </Stack>
    <Stack sx={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
      <ExpertAvatar size={112} />
    </Stack>
    <Stack
      sx={{ alignItems: 'center', justifyContent: 'space-between' }}
      direction='row'
    >
      <Stack>
        <Typography sx={{ color: 'custom.neutral.50' }} variant='h5'>
          {EXPERT.name}
        </Typography>
        <Typography
          sx={{ color: 'custom.neutral.200', whiteSpace: 'nowrap' }}
          variant='caption'
        >
          {EXPERT.role}
        </Typography>
      </Stack>
      <Box
        sx={{
          width: 40,
          height: 40,
          display: 'grid',
          placeItems: 'center',
          color: 'custom.neutral.50',
          bgcolor: 'custom.cherry.500',
          borderRadius: '50%',
        }}
      >
        <Icon icon='phone' fontSize={16} />
      </Box>
    </Stack>
  </Stack>
);

/** A phone ringing: the care expert calling in. The one piece on a device. */
const IncomingCall = () => (
  <PhoneFrame scale={0.6}>
    <Stack sx={{ height: '100%', bgcolor: 'custom.marineGreen.700' }}>
      <StatusBar />
      <Stack sx={{ flex: 1, alignItems: 'center', pt: 10 }} spacing={2}>
        <ExpertAvatar size={120} />
        <Typography sx={{ color: 'custom.neutral.50' }} variant='h2'>
          {EXPERT.name}
        </Typography>
        <Typography sx={{ color: 'custom.neutral.200' }} variant='body1'>
          {INCOMING.label}
        </Typography>
      </Stack>
      <Stack sx={{ justifyContent: 'space-around', pb: 12 }} direction='row'>
        {(
          [
            [INCOMING.decline, 'custom.cherry.500', 'custom.neutral.50'],
            [INCOMING.accept, 'custom.limeGreen.500', 'custom.marineGreen.700'],
          ] as const
        ).map(([label, bg, fg]) => (
          <Stack key={label} sx={{ alignItems: 'center' }} spacing={2}>
            <Box
              sx={{
                width: 72,
                height: 72,
                display: 'grid',
                placeItems: 'center',
                bgcolor: bg,
                color: fg,
                borderRadius: '50%',
              }}
            >
              <Icon icon='phone' fontSize={28} />
            </Box>
            <Typography sx={{ color: 'custom.neutral.50' }} variant='body2'>
              {label}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  </PhoneFrame>
);

/** The expert's face with a new-activity badge, and what the activity is. */
const FaceWithNews = ({ title, body }: Extract<Piece, { kind: 'face' }>) => (
  <Stack sx={{ alignItems: 'center' }} direction='row' spacing={4}>
    <Badge
      color='primary'
      overlap='circular'
      variant='dot'
      sx={{ '& .MuiBadge-dot': { width: 18, height: 18, borderRadius: 9 } }}
    >
      <ExpertAvatar size={80} />
    </Badge>
    <Card sx={{ flex: 1, p: PAD }}>
      <TitleBody title={title} body={body} />
    </Card>
  </Stack>
);

const PieceView = ({ piece }: { piece: Piece }) => {
  switch (piece.kind) {
    case 'push':
      return <Push {...piece} />;
    case 'chat':
      return <Chat {...piece} />;
    case 'bubbles':
      return <Bubbles {...piece} />;
    case 'reminder':
      return <Reminder {...piece} />;
    case 'button':
      return (
        <Box>
          <Button ariaLabel={piece.label} iconLeft='phone' size='lg'>
            {piece.label}
          </Button>
        </Box>
      );
    case 'call':
      return <CallTile />;
    case 'incoming':
      return <IncomingCall />;
    case 'face':
      return <FaceWithNews {...piece} />;
  }
};

/** How wide each kind sits when it stands alone or in a cluster. */
const WIDTH: Record<Piece['kind'], number | 'auto'> = {
  push: 360,
  chat: 380,
  bubbles: 380,
  reminder: 380,
  button: 'auto',
  call: 240,
  incoming: 'auto',
  face: 400,
};

// ── 1. Main banners ───────────────────────────────────────────────────────

const BANNER = { width: 1200, height: 800 };
const PHONE_SCALE = 0.85;
const PHONE_W = 403 * PHONE_SCALE;

/**
 * Two columns either side of the phone, each tucked over its edge by the same
 * amount and centred on it, with an even gap between the cards in a column.
 * Cards never sit on each other; the only overlap is with the blurred app,
 * which is what says they came from it.
 *
 * `left` is how many of the pieces go in the left column, in order; the rest
 * go right. `cascade` puts the phone on the left and everything beside it.
 */
const LAYOUTS: Record<BannerLayout, { phoneX: number; left: number }> = {
  classic: { phoneX: (BANNER.width - PHONE_W) / 2, left: 1 },
  call: { phoneX: (BANNER.width - PHONE_W) / 2, left: 1 },
  cascade: { phoneX: 150, left: 0 },
  homepage: { phoneX: (BANNER.width - PHONE_W) / 2, left: 2 },
};

/** How far a column reaches over the phone's edge. */
const TUCK = 56;

/** The app, there but out of focus: it says this is a product, nothing more. */
const BlurredApp = ({ x }: { x: number }) => (
  <Box
    sx={{
      position: 'absolute',
      left: x,
      top: 30,
      opacity: 0.5,
      filter: 'blur(5px)',
      pointerEvents: 'none',
    }}
  >
    <PhoneFrame scale={PHONE_SCALE}>
      <HomeScreen />
    </PhoneFrame>
  </Box>
);

const Banner = ({
  label,
  layout,
  pieces,
}: {
  label: string;
  layout: BannerLayout;
  pieces: Piece[];
}) => {
  const { phoneX, left } = LAYOUTS[layout];
  const column = (side: 'left' | 'right', items: Piece[]) => (
    <Stack
      sx={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        justifyContent: 'center',
        alignItems: side === 'left' ? 'flex-end' : 'flex-start',
        ...(side === 'left'
          ? { right: BANNER.width - phoneX - TUCK }
          : { left: phoneX + PHONE_W - TUCK }),
      }}
      spacing={5}
    >
      {items.map((piece, i) => (
        <Box key={i} sx={{ width: WIDTH[piece.kind] }}>
          <PieceView piece={piece} />
        </Box>
      ))}
    </Stack>
  );
  return (
    <Frame
      label={label}
      width={BANNER.width}
      height={BANNER.height}
      ground='transparent'
    >
      <BlurredApp x={phoneX} />
      {left > 0 && column('left', pieces.slice(0, left))}
      {column('right', pieces.slice(left))}
    </Frame>
  );
};

// ── 2–4. Pullouts and clusters ────────────────────────────────────────────

/**
 * A clear canvas that fits what is on it, with an even margin, so the export
 * is the picture and not the picture in a field of nothing.
 */
const HugFrame = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Stack spacing={2}>
    <SectionTitle label={label} />
    <Box
      sx={{
        width: 'fit-content',
        p: 6,
        border: 1,
        borderStyle: 'dashed',
        borderColor: 'custom.neutral.300',
        borderRadius: `${radius.md}px`,
      }}
    >
      {children}
    </Box>
  </Stack>
);

/**
 * One column, left aligned, with the same small gap between every piece. Close
 * enough to read as one picture on a clear ground, apart enough that nothing
 * sits on anything else.
 */
const Stacked = ({ pieces }: { pieces: Piece[] }) => (
  <Stack sx={{ alignItems: 'flex-start' }} spacing={3}>
    {pieces.map((piece, i) => (
      <Box key={i} sx={{ width: WIDTH[piece.kind] }}>
        <PieceView piece={piece} />
      </Box>
    ))}
  </Stack>
);

const Cluster = ({ picture }: { picture: Picture }) => (
  <HugFrame label={picture.label}>
    <Stacked pieces={picture.pieces} />
  </HugFrame>
);

/** One piece on its own, at the width it would sit at on a phone. */
const Pullout = ({ picture }: { picture: Picture }) => (
  <HugFrame label={picture.label}>
    {picture.pieces.map((piece, i) => (
      <Box key={i} sx={{ width: WIDTH[piece.kind] }}>
        <PieceView piece={piece} />
      </Box>
    ))}
  </HugFrame>
);

const Section = ({
  title,
  body,
  children,
}: {
  title: string;
  body: string;
  children: React.ReactNode;
}) => (
  <Stack spacing={6}>
    <Stack spacing={1}>
      <Typography variant='h2'>{title}</Typography>
      <Typography sx={{ color: 'text.secondary' }} variant='body1'>
        {body}
      </Typography>
    </Stack>
    {children}
  </Stack>
);

const Row = ({ children }: { children: React.ReactNode }) => (
  <Stack
    sx={{ alignItems: 'flex-start', flexWrap: 'wrap' }}
    direction='row'
    useFlexGap
    spacing={6}
  >
    {children}
  </Stack>
);

const SiteHeaders = () => (
  <Box sx={{ px: 6, py: 7, overflowX: 'auto' }}>
    <Stack spacing={10}>
      <Section {...SECTIONS.banners}>
        {BANNERS.map((page) => (
          <Stack key={page.id} spacing={4}>
            <Typography variant='h4'>{page.label}</Typography>
            <Row>
              {page.variants.map((variant, i) => (
                <Banner
                  key={i}
                  label={`${page.label} · ${String.fromCharCode(65 + i)}`}
                  layout={variant.layout}
                  pieces={variant.pieces}
                />
              ))}
            </Row>
          </Stack>
        ))}
      </Section>

      <Section {...SECTIONS.homeServices}>
        <Row>
          {HOME_SERVICES.map((picture) => (
            <Pullout key={picture.id} picture={picture} />
          ))}
        </Row>
      </Section>

      <Section {...SECTIONS.serviceClusters}>
        <Row>
          {SERVICE_CLUSTERS.map((picture) => (
            <Cluster key={picture.id} picture={picture} />
          ))}
        </Row>
      </Section>

      <Section {...SECTIONS.sectors}>
        {(
          [
            [SECTIONS.employers, EMPLOYER_PULLOUTS],
            [SECTIONS.banks, BANK_PULLOUTS],
            [SECTIONS.general, GENERAL_PULLOUTS],
          ] as const
        ).map(([heading, pictures]) => (
          <Stack key={heading} spacing={4}>
            <Typography variant='h4'>{heading}</Typography>
            <Row>
              {pictures.map((picture) => (
                <Pullout key={picture.id} picture={picture} />
              ))}
            </Row>
          </Stack>
        ))}
      </Section>
    </Stack>
  </Box>
);

export default SiteHeaders;
